import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Link2, ExternalLink, CheckCircle2, AlertCircle, Plus, Pencil, Eye, Trash2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import LinkModal from '@/components/LinkModal';
import DocumentPreviewModal from '@/components/DocumentPreviewModal';
import TabExplainer from '@/components/common/TabExplainer';
import CloudFilePicker from '@/components/links/CloudFilePicker';
import { toast } from 'sonner';

export default function ClientLinks() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [links, setLinks] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [previewLink, setPreviewLink] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchData = async () => {
    if (!token || !clientId) return;
    try {
      const [linksRes, entRes] = await Promise.all([
        api.getLinks(token, clientId),
        api.getEntities(token, clientId)
      ]);
      setLinks(linksRes.data);
      setEntities(entRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [token, clientId]);

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity?.name;
  };

  const handleAddLink = () => {
    setEditingLink(null);
    setShowLinkModal(true);
  };

  const handleEditLink = (link) => {
    setEditingLink(link);
    setShowLinkModal(true);
  };

  const handleSaveLink = async (linkData) => {
    try {
      if (editingLink) {
        // Edit existing link - save to database
        await api.updateLink(token, clientId, editingLink.id, linkData);
        toast.success('Link updated successfully');
      } else {
        // Create new link - save to database
        await api.createLink(token, clientId, linkData);
        toast.success('Link added successfully');
      }
      await fetchData();
      setShowLinkModal(false);
      setEditingLink(null);
    } catch (error) {
      console.error('Failed to save link:', error);
      toast.error('Failed to save link');
    }
  };

  const handleDeleteLink = async (link) => {
    try {
      await api.deleteLink(token, clientId, link.id);
      toast.success('Link deleted successfully');
      await fetchData();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete link:', error);
      toast.error('Failed to delete link');
    }
  };

  // Convert link to document format for preview modal
  const getLinkAsDocument = (link) => ({
    title: link.title,
    category: link.category,
    sourceSystem: link.sourceSystem,
    lastUpdated: link.lastVerified,
    owner: link.owner
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="links-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Links & Sources</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-[#5D524D]">
              Pointers to files held elsewhere &mdash; never duplicated, just kept current.
            </p>
            <TabExplainer
              label="About Links & Sources"
              testId="links-tab-explainer"
              title="What is Links & Sources?"
              intro="Pointers to data living elsewhere — Dropbox, Google Drive, SharePoint, broker portals, the family's solicitor. QIndex remembers where each item lives and when it was last reviewed; it does not host the file itself."
              useFor={[
                'Live bank, broker and super-fund statements that update on their portal',
                "Large media libraries (photos, videos) you don't want to re-upload",
                "Source-of-truth originals held by a solicitor / accountant / custodian",
                "Anything you need to know exists, but shouldn't duplicate into QIndex",
              ]}
              notFor={[
                'Documents you want indexed, AI-categorised or portal-shared — use Documents',
              ]}
              useCases={[
                'Will held at Maddocks — review every 12 months',
                'CommSec brokerage portal — verify quarterly',
                'Family Office Notion vault for advisers',
              ]}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CloudFilePicker
            onSaved={fetchData}
            data-testid="cloud-file-picker"
          />
          <Button 
            className="caffeine-btn" 
            onClick={handleAddLink}
            data-testid="add-link-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Link
          </Button>
        </div>
      </div>

      {/* Links Table */}
      {links.length > 0 ? (
        <div className="caffeine-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Document/Link</th>
                <th>Entity</th>
                <th>Source</th>
                <th>Category</th>
                <th>Owner</th>
                <th>Review Cadence</th>
                <th>Status</th>
                <th>Last Verified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.map(link => (
                <tr key={link.id} data-testid={`link-row-${link.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-[#8C8078]" />
                      <span className="font-medium text-[#2C2420]">{link.title}</span>
                    </div>
                  </td>
                  <td>
                    {link.entityId ? (
                      <span 
                        className="text-[#4A3B32] cursor-pointer hover:underline"
                        onClick={() => navigate(`/app/clients/${clientId}/entities/${link.entityId}`)}
                      >
                        {getEntityName(link.entityId)}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <span className="source-chip">{link.sourceSystem}</span>
                  </td>
                  <td>{link.category}</td>
                  <td>{link.owner}</td>
                  <td>{link.reviewCadence}</td>
                  <td>
                    <span className={link.status === 'Verified' ? 'badge-verified' : 'badge-needs-review'}>
                      {link.status === 'Verified' ? (
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      ) : (
                        <AlertCircle className="w-3.5 h-3.5 mr-1" />
                      )}
                      {link.status}
                    </span>
                  </td>
                  <td>{formatDateAU(link.lastVerified)}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="caffeine-btn-ghost h-8 w-8 p-0"
                        onClick={() => setPreviewLink(link)}
                        data-testid={`preview-link-${link.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="caffeine-btn-ghost h-8 w-8 p-0"
                        onClick={() => handleEditLink(link)}
                        data-testid={`edit-link-${link.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="caffeine-btn-ghost h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        onClick={() => setDeleteConfirm(link)}
                        data-testid={`delete-link-${link.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="caffeine-btn-ghost h-8 w-8 p-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Link2 className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No links indexed</p>
          <p className="text-sm">No documents or data sources indexed for this client</p>
          <Button 
            className="caffeine-btn mt-4" 
            onClick={handleAddLink}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add First Link
          </Button>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <LinkModal
          link={editingLink}
          entities={entities}
          onClose={() => {
            setShowLinkModal(false);
            setEditingLink(null);
          }}
          onSave={handleSaveLink}
        />
      )}

      {/* Preview Modal */}
      {previewLink && (
        <DocumentPreviewModal
          document={getLinkAsDocument(previewLink)}
          type="governance"
          onClose={() => setPreviewLink(null)}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteLink(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
